import { Button } from "./ui/button";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Lock, FileEdit, Trash2, X, Loader2 } from "lucide-react";
import { useLang } from "../context/LangContext";

/**
 * 4-option save dialog used by both daily ledgers (#91).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange(v): close handler
 *  - saving: boolean — disables all action buttons & shows spinner
 *  - onSaveFinal(): lock the record permanently
 *  - onSaveEditable(): save as draft (editable later)
 *  - onIgnore(): discard changes and leave
 *  - testIdPrefix: e.g. "daily" → produces `daily-save-final-btn`, etc.
 */
export default function SaveDialog({
    open,
    onOpenChange,
    saving = false,
    onSaveFinal,
    onSaveEditable,
    onIgnore,
    testIdPrefix = "save",
}) {
    const { t } = useLang();
    const tid = (name) => `${testIdPrefix}-save-${name}`;
    const stop = (fn) => (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!saving) fn?.();
    };

    return (
        <AlertDialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange?.(v); }}>
            <AlertDialogContent dir={t.dir} data-testid={tid("dialog")}>
                <AlertDialogHeader className="text-start">
                    <AlertDialogTitle>{t.saveDialogTitle}</AlertDialogTitle>
                    <AlertDialogDescription>{t.saveDialogSubtitle}</AlertDialogDescription>
                </AlertDialogHeader>

                <div className="mt-2 space-y-2">
                    {/* Save Final */}
                    <button
                        type="button"
                        onClick={stop(onSaveFinal)}
                        disabled={saving}
                        className="group flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-3 text-start transition hover:border-neutral-900 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid={tid("final-btn")}
                    >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-white">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-neutral-900">{t.saveOptionFinal}</div>
                            <div className="text-xs text-neutral-500">{t.saveOptionFinalHint}</div>
                        </div>
                    </button>

                    {/* Save Editable */}
                    <button
                        type="button"
                        onClick={stop(onSaveEditable)}
                        disabled={saving}
                        className="group flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-3 text-start transition hover:border-amber-500 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid={tid("editable-btn")}
                    >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-600 text-white">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileEdit className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-amber-900">{t.saveOptionEditable}</div>
                            <div className="text-xs text-amber-800/70">{t.saveOptionEditableHint}</div>
                        </div>
                    </button>

                    {/* Ignore */}
                    <button
                        type="button"
                        onClick={stop(onIgnore)}
                        disabled={saving}
                        className="group flex w-full items-center gap-3 rounded-lg border border-red-200 bg-red-50/40 px-3 py-3 text-start transition hover:border-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid={tid("ignore-btn")}
                    >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white">
                            <Trash2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-red-900">{t.saveOptionIgnore}</div>
                            <div className="text-xs text-red-800/70">{t.saveOptionIgnoreHint}</div>
                        </div>
                    </button>
                </div>

                <div className="mt-3 flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={saving}
                        onClick={() => onOpenChange?.(false)}
                        data-testid={tid("cancel-btn")}
                    >
                        <X className="me-1 h-4 w-4" />
                        {t.cancel}
                    </Button>
                </div>
            </AlertDialogContent>
        </AlertDialog>
    );
}
